import { useLocation } from 'react-router-dom';
import { conciergeRequestFromSearch } from '../lib/concierge-link';
import { useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import { ConciergeProjectWorkspace } from '../components/ConciergeProjectWorkspace';
const statuses = [
  'received',
  'reviewing',
  'awaiting-customer',
  'sourcing-installer',
  'quoting',
  'approved',
  'scheduled',
  'installing',
  'completed',
  'closed',
];
const label = (value: string) => value.replace(/-/g, ' ');
export function ConciergePage() {
  const api = useApi();
  const location = useLocation();
  const [rows, setRows] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [queueOpen, setQueueOpen] = useState(true);
  async function load(more = false) {
    setBusy(true);
    setError('');
    try {
      const result = await api.concierge(
        more && cursor ? '?cursor=' + encodeURIComponent(cursor) : '',
      );
      setRows((previous) =>
        more ? [...previous, ...result.requests] : result.requests,
      );
      setCursor(result.cursor);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function open(id: string) {
    setBusy(true);
    setError('');
    try {
      const result = await api.concierge('/' + id);
      setSelected(result.request);
      setQueueOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void (async () => {
      await load();
      const request = conciergeRequestFromSearch(location.search);
      if (request) await open(request);
    })();
  }, [location.search]);
  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    try {
      const latest = await api.concierge('/' + selected.consultationId);
      const result = await api.concierge('/' + selected.consultationId, {
        method: 'PATCH',
        body: JSON.stringify({
          revision: latest.request.revision || 0,
          status: latest.request.workflow?.status || 'received',
          owner: data.get('owner'),
          note: data.get('note'),
        }),
      });
      setSelected(result.request);
      setRows((previous) =>
        previous.map((row) =>
          row.consultationId === result.request.consultationId
            ? result.request
            : row,
        ),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function download(file: any) {
    setBusy(true);
    setError('');
    try {
      const result = await api.concierge(
        `/${selected.consultationId}/files/${file.attachmentId}/download`,
        { method: 'POST', body: '{}' },
      );
      const url = new URL(result.url);
      if (
        url.protocol !== 'https:' ||
        !/^manor-concierge-media-(test-219443365041|prod-680511694845)\.s3\.(ca-central-1|us-east-1)\.amazonaws\.com$/.test(
          url.hostname,
        )
      )
        throw Error('Unexpected download destination.');
      const a = document.createElement('a');
      a.href = result.url;
      a.rel = 'noreferrer';
      a.click();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const input =
    'w-full min-h-11 rounded-md border border-slate-300 bg-white p-3 text-sm';
  return (
    <section>
      <div className="flex flex-wrap justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Concierge requests</h1>
          <p className="text-slate-600 mt-2">
            Review projects, manage follow-up, and prepare quotes.
          </p>
        </div>
        <button
          disabled={busy}
          onClick={() => void load()}
          className="border rounded-md px-4 py-2"
        >
          Refresh queue
        </button>
      </div>
      {error && (
        <p role="alert" className="p-4 mb-4 border border-red-300 text-red-800">
          {error}
        </p>
      )}
      <div className="grid lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0">
          {selected && (
            <button
              type="button"
              className="mb-3 flex min-h-11 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium lg:hidden"
              aria-expanded={queueOpen}
              onClick={() => setQueueOpen(!queueOpen)}
            >
              <span>Request queue ({rows.length})</span>
              <span>{queueOpen ? 'Hide' : 'Browse'}</span>
            </button>
          )}
          <div className={queueOpen || !selected ? '' : 'hidden lg:block'}>
            <label className="block mb-3">
              Search loaded requests
              <input
                className={input}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, email, or reference"
              />
            </label>
            <label className="block mb-4">
              Status
              <select
                className={input}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="">All statuses</option>
                {statuses.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <div className="space-y-2">
              {rows
                .filter(
                  (row) =>
                    (!filter ||
                      (row.workflow?.status || 'received') === filter) &&
                    [row.name, row.email, row.consultationId]
                      .join(' ')
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                )
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map((row) => (
                  <button
                    key={row.consultationId}
                    disabled={busy}
                    onClick={() => void open(row.consultationId)}
                    className={`text-left w-full rounded-lg border p-4 break-words ${selected?.consultationId === row.consultationId ? 'border-slate-800 bg-slate-100' : 'bg-white border-slate-200'}`}
                  >
                    <strong className="block">{row.name}</strong>
                    <span className="block text-sm break-all">{row.email}</span>
                    <span className="block text-sm text-slate-600 mt-2">
                      {label(row.workflow?.status || 'received')} · {row.city}
                    </span>
                  </button>
                ))}
            </div>
            {!rows.length && !busy && <p>No requests loaded.</p>}
            {cursor && (
              <button
                disabled={busy}
                onClick={() => void load(true)}
                className="border rounded-md p-3 mt-4"
              >
                Load more requests
              </button>
            )}
            {busy && (
              <p role="status" className="mt-3">
                Loading…
              </p>
            )}
          </div>
        </aside>
        <article className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
          {!selected ? (
            <p>Select a request to review the customer’s project and files.</p>
          ) : (
            <>
              <h2 className="text-xl font-semibold">{selected.name}</h2>
              <p className="text-sm text-slate-500 break-all mt-2">
                {selected.consultationId}
              </p>
              <p className="text-sm mt-2">
                Received {new Date(selected.createdAt).toLocaleString()}
              </p>
              <p className="mt-2 break-all text-sm text-slate-600">
                {selected.email}
              </p>
              <ConciergeProjectWorkspace
                key={selected.consultationId}
                request={selected}
                onStatusChange={(status) => {
                  setRows((previous) =>
                    previous.map((row) =>
                      row.consultationId === selected.consultationId
                        ? { ...row, workflow: { ...row.workflow, status } }
                        : row,
                    ),
                  );
                }}
              />
              <details className="mt-8 border-t pt-5">
                <summary className="min-h-11 cursor-pointer text-base font-semibold">
                  Original application, files & request tracking
                </summary>
                <h3 className="font-semibold mt-6 mb-3">
                  Customer profile and project
                </h3>
                <dl className="space-y-3">
                  {Object.entries(selected)
                    .filter(
                      ([key]) =>
                        ![
                          'attachments',
                          'workflow',
                          'revision',
                          'consultationId',
                          'name',
                          'createdAt',
                          'customerSubject',
                        ].includes(key),
                    )
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className="grid sm:grid-cols-[150px_minmax(0,1fr)] gap-1 sm:gap-4"
                      >
                        <dt className="text-sm text-slate-500">
                          {key === 'status'
                            ? 'Submission status'
                            : key
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (value) => value.toUpperCase())}
                        </dt>
                        <dd className="text-sm whitespace-pre-wrap break-words">
                          {Array.isArray(value)
                            ? value.join(', ')
                            : String(value)}
                        </dd>
                      </div>
                    ))}
                </dl>
                <h3 className="font-semibold mt-8 mb-3">
                  Project files ({selected.attachments.length})
                </h3>
                <p className="text-sm text-slate-500 mb-3">
                  Private downloads expire after one minute. Review files safely
                  before opening or sharing with an installer.
                </p>
                {selected.attachments.map((file: any) => (
                  <div
                    key={file.attachmentId}
                    className="flex flex-wrap items-center justify-between gap-3 border-t py-3"
                  >
                    <span className="min-w-0 break-all text-sm">
                      {file.fileName} ·{' '}
                      {file.size < 1024 * 1024
                        ? `${Math.max(1, Math.ceil(file.size / 1024))} KB`
                        : `${(file.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                    <button
                      disabled={busy}
                      onClick={() => void download(file)}
                      className="border rounded-md px-4 py-2 text-sm"
                    >
                      Download
                    </button>
                  </div>
                ))}
                <form
                  key={`${selected.consultationId}-${selected.revision || 0}`}
                  onSubmit={save}
                  className="mt-8 border-t pt-6 space-y-4"
                >
                  <h3 className="font-semibold">
                    Request ownership & private note
                  </h3>
                  <label className="block">
                    Assigned owner
                    <input
                      name="owner"
                      maxLength={120}
                      className={input}
                      defaultValue={selected.workflow?.owner || ''}
                      placeholder="Team member or email"
                    />
                  </label>
                  <label className="block">
                    Internal note
                    <textarea
                      name="note"
                      maxLength={4000}
                      rows={4}
                      className={input}
                      placeholder="Follow-up, installer sourcing, quote details, or next steps"
                    />
                  </label>
                  <p className="text-sm text-slate-500">
                    Internal tracking only. Record customer approval before
                    scheduling an installation.
                  </p>
                  <button
                    disabled={busy}
                    className="bg-slate-900 text-white rounded-md px-5 py-3"
                  >
                    Save update
                  </button>
                </form>
                <h3 className="font-semibold mt-8 mb-3">Activity</h3>
                <ol className="space-y-4">
                  {[...(selected.workflow?.history || [])]
                    .reverse()
                    .map((entry: any) => (
                      <li key={entry.id} className="border-l-2 pl-4">
                        <p className="text-sm font-medium">
                          {label(entry.status)}
                          {entry.owner && ` · ${entry.owner}`}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {entry.actor} · {new Date(entry.at).toLocaleString()}
                        </p>
                        {entry.note && (
                          <p className="text-sm whitespace-pre-wrap break-words mt-2">
                            {entry.note}
                          </p>
                        )}
                      </li>
                    ))}
                </ol>
              </details>
            </>
          )}
        </article>
      </div>
    </section>
  );
}
